import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Plumber",
  "Electrician",
  "Glazier / Window Fitter",
  "Carpenter",
  "Builder",
  "Gas Engineer",
  "Roofer",
  "Painter & Decorator",
  "Landscaper",
  "Fumigator / Pest Control",
  "Mobile Haircare",
  "Massage Therapist",
  "Domestic Cleaner",
  "Other solo business",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.business_type || !form.message) {
      toast.error("Please fill in every field — we promise it's quick.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Sent! We'll be in touch within one working day.");
      setForm({ name: "", email: "", business_type: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || "Couldn't send right now. Try again?",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-b-2 border-ink py-24 lg:py-32 bg-[var(--ink)] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 confetti-bg opacity-10 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-2">
          <p className="uppercase text-sm font-bold tracking-[0.25em] text-[var(--yellow)]">
            Let's talk
          </p>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl font-black tracking-tight">
            Book your{" "}
            <span className="text-[var(--pink)]">free consultation.</span>
          </h2>
          <p className="mt-5 text-stone-300 text-lg leading-relaxed">
            30 minutes. No sales pitch. Just a friendly chat about your business
            and where AI could save you time. If we can't help, we'll say so.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href="mailto:eventinfo@iariya.com"
              data-testid="contact-email-link"
              className="flex items-center gap-4 group"
            >
              <div className="h-12 w-12 rounded-xl border-2 border-white/30 bg-white/10 flex items-center justify-center group-hover:bg-[var(--pink)] transition-colors">
                <Mail size={20} />
              </div>
              <div>
                <p className="text-sm text-stone-400 uppercase tracking-wider font-bold">
                  Email
                </p>
                <p className="font-display text-xl font-bold">
                  eventinfo@iariya.com
                </p>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl border-2 border-white/30 bg-white/10 flex items-center justify-center">
                <MapPin size={20} />
              </div>
              <div>
                <p className="text-sm text-stone-400 uppercase tracking-wider font-bold">
                  Based in
                </p>
                <p className="font-display text-xl font-bold">
                  United Kingdom · Remote-first
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="bg-white text-ink border-2 border-ink rounded-3xl p-7 sm:p-9 shadow-brutal-lg space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-bold mb-2">
                  Your name
                </label>
                <input
                  data-testid="contact-input-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Alex Smith"
                  className="w-full border-2 border-ink rounded-xl px-4 py-3 bg-[var(--paper)] focus:outline-none focus:bg-[var(--yellow)]/30 font-medium"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Email</label>
                <input
                  data-testid="contact-input-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="alex@yourbusiness.co.uk"
                  className="w-full border-2 border-ink rounded-xl px-4 py-3 bg-[var(--paper)] focus:outline-none focus:bg-[var(--yellow)]/30 font-medium"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">
                What's your trade?
              </label>
              <select
                data-testid="contact-input-business-type"
                value={form.business_type}
                onChange={update("business_type")}
                className="w-full border-2 border-ink rounded-xl px-4 py-3 bg-[var(--paper)] focus:outline-none focus:bg-[var(--yellow)]/30 font-medium"
              >
                <option value="">Select your trade…</option>
                {BUSINESS_TYPES.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold mb-2">
                Tell us what's eating your time
              </label>
              <textarea
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                rows={5}
                placeholder="e.g. I'm missing 3-4 calls a day, and I spend my evenings writing quotes…"
                className="w-full border-2 border-ink rounded-xl px-4 py-3 bg-[var(--paper)] focus:outline-none focus:bg-[var(--yellow)]/30 font-medium resize-none"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={submitting}
              className="btn-ink w-full inline-flex items-center justify-center gap-2 bg-[var(--pink)] text-white px-6 py-4 text-base disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending…
                </>
              ) : sent ? (
                <>Sent — we'll reply soon!</>
              ) : (
                <>
                  Send my enquiry <Send size={18} />
                </>
              )}
            </button>
            <p className="text-xs text-stone-600 text-center">
              We reply within one working day. No spam, ever.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
